import { useCallback, useMemo } from 'react';
import { FileError } from 'react-dropzone';
import { useRouteMatch } from 'react-router';
import { AxiosResponse } from 'axios';
import { toNumber, toString } from 'lodash';

import { api } from '@/api';
import { FileImage } from '@/common/constants';
import { getUploadFileErrorCode } from '@/components/elements/DropScan/utils/getUploadFileErrorCode';
import {
  API_URL,
  ICreateDocumentAggregatorDto,
  ICreateDocumentAggregatorDtoDocumentTypeEnum,
  IDocumentIdDto,
} from '@/dtos';
import useCompleteFileUploadQuery from '@/modules/ReimburseMe/queries/useCompleteFileUpload.query';
import useCreateFileUploadUrlQuery from '@/modules/ReimburseMe/queries/useCreateFileUploadUrl.query';
import useUploadFileToS3Query from '@/modules/ReimburseMe/queries/useUploadFileToS3.query';
import { ICreateDocumentDtoDocumentTypeEnum } from '@/types';
import { getFileType } from '@/utils/modifiers/getFileType';

export const getBlob = async (url: string) =>
  fetch(url)
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    .then(async (response: any) => {
      const blob = await response?.arrayBuffer();

      return new Blob([blob], { type: getFileType(url) });
    });

const useFileUploadQuery = () => {
  const {
    params: { id: employeeId },
  } = useRouteMatch<{ id: string }>();
  const { createFileUploadUrl } = useCreateFileUploadUrlQuery();
  const { uploadFileToS3 } = useUploadFileToS3Query();
  const { completeFileUpload } = useCompleteFileUploadQuery();

  const createDocumentId = useCallback(
    async (documentType?: ICreateDocumentDtoDocumentTypeEnum) => {
      try {
        const response = await api.post<
          IDocumentIdDto,
          AxiosResponse<IDocumentIdDto>,
          ICreateDocumentAggregatorDto
        >(API_URL.DOCUMENTS, {
          employee_id: toNumber(employeeId),
          document_type: (documentType ||
            ICreateDocumentAggregatorDtoDocumentTypeEnum.RECEIPT) as ICreateDocumentAggregatorDtoDocumentTypeEnum,
        });
        return `${response?.data?.document_id || ''}`;
      } catch {
        return '';
      }
    },
    [employeeId]
  );

  const uploadFile = useCallback(
    async (file: FileImage, documentId: string, ocrRequired = false, onError?: (error?: FileError) => void) => {
      if (!documentId) {
        return file;
      }

      try {
        const uploadUrl = await createFileUploadUrl({
          documentId,
          fileName: file.name,
          employeeId: employeeId,
        });

        if (!uploadUrl?.data?.upload_url) {
          return;
        }

        const previewResponse = await fetch(file.preview);
        const fileBlob = await previewResponse.blob();
        await uploadFileToS3({ fileBlob, upload_url: uploadUrl?.data?.upload_url });

        const response = await completeFileUpload({
          documentId,
          employeeId: employeeId,
          fileName: file.name,
          objectKey: uploadUrl?.data?.object_key,
          ocrRequired,
        });

        const responseBlob = await getBlob(response?.data?.s3_url || '');

        return {
          ...file,
          id: toString(response?.data?.id),
          url: response?.data?.s3_url || '',
          blob: responseBlob,
          preview: URL.createObjectURL(responseBlob),
        } as FileImage;
      } catch (error) {
        const errorCode = getUploadFileErrorCode(error);

        if (errorCode) {
          onError?.({ code: errorCode, message: '' });
          return null;
        }

        if (onError) {
          onError();
        }
        return null;
      }
    },
    [employeeId, completeFileUpload, createFileUploadUrl, uploadFileToS3]
  );

  return useMemo(
    () => ({
      createDocumentId,
      uploadFiles: async (
        documentId: string,
        files: FileImage[],
        ocrRequired = false,
        onFileUploadError?: (error?: FileError) => void
      ) => {
        const ids: FileImage[] = [];

        const uploadFileRecursive = async (fileIndex: number = 0) => {
          const uploadedFile = await uploadFile(files[fileIndex], documentId, ocrRequired, onFileUploadError);
          if (uploadedFile) {
            ids.push(uploadedFile);
            if (files[fileIndex + 1]) {
              await uploadFileRecursive(fileIndex + 1);
            }
          }
        };

        await uploadFileRecursive();
        return ids;
      },
    }),
    [createDocumentId, uploadFile]
  );
};

export default useFileUploadQuery;
